import React from "react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

// v0.6.5 — freeze confirmation dialog extracted from Step6FreezeExport.jsx
// (500-line cap, mechanical split, no behaviour change) to make room for
// the new Supersede dialog on the same screen.
export const FreezeConfirmDialog = ({
  open, onOpenChange, coverage, fxAmend,
  hasNetNegative, negativeCountries, repaymentConfirmed, setRepaymentConfirmed,
  hasRefundChannelMismatch, refundChannelMismatchCount, mismatchAcknowledged, setMismatchAcknowledged,
  freezeError, freezing, doFreeze,
}) => {
  const { t } = useTranslation();
  return (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent data-testid="freeze-confirm-dialog" className="bg-white dark:bg-slate-900">
      <AlertDialogHeader>
        <AlertDialogTitle>{t("ui.freeze.confirm.title", { defaultValue: "Freeze & lock this filing?" })}</AlertDialogTitle>
        <AlertDialogDescription asChild>
          <div className="space-y-3 text-xs">
            <p className="text-slate-600 dark:text-slate-300">
              {t("ui.freeze.confirm.permanent_before", { defaultValue: "Freezing is" })}{" "}
              <strong>{t("ui.freeze.confirm.permanent_strong", { defaultValue: "permanent" })}</strong>{" "}
              {t("ui.freeze.confirm.permanent_after", {
                defaultValue: "— it locks the filing reference and audit trail. To change anything afterwards you must run a new calc and supersede this one.",
              })}
            </p>
            <div className="tabular-nums text-[11px] border border-slate-200 dark:border-slate-700 rounded-sm divide-y divide-slate-100 dark:divide-slate-800">
              <div className="flex justify-between px-3 py-1.5">
                <span className="text-slate-500">{t("ui.filing.coverage.title", { defaultValue: "Registration coverage" })}</span>
                <span data-testid="freeze-coverage-open" className={(coverage?.open_gap_count || 0) > 0 ? "text-amber-600 dark:text-amber-400 font-bold" : "text-emerald-600 dark:text-emerald-400"}>
                  {(coverage?.open_gap_count || 0) > 0
                    ? t("ui.freeze.confirm.open_gaps", { count: coverage.open_gap_count, defaultValue: `${coverage.open_gap_count} open gap(s)` })
                    : t("ui.freeze.confirm.no_open_gaps", { defaultValue: "no open gaps" })}
                </span>
              </div>
              {(coverage?.acknowledged_gaps || []).map((g) => (
                <div key={g.gap_key} data-testid={`freeze-coverage-ack-${g.gap_key}`} className="px-3 py-1.5">
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-300">{g.country || "OSS"} · VAT {Number(g.liability_vat).toFixed(2)}</span>
                    <span className="text-emerald-600 dark:text-emerald-400">{t("ui.filing.status.acknowledged", { defaultValue: "acknowledged" })}</span>
                  </div>
                  <div className="text-slate-400 truncate">“{g.ack_note}” — {g.ack_by}</div>
                </div>
              ))}
            </div>
            {(fxAmend?.reinvoiced_count || 0) > 0 && (
              <div className="tabular-nums text-[11px] border border-teal-200 dark:border-teal-800 rounded-sm" data-testid="freeze-fx-amendments">
                <div className="flex justify-between px-3 py-1.5 border-b border-teal-100 dark:border-teal-900">
                  <span className="text-slate-500">{t("ui.freeze.confirm.fx_amendments", { defaultValue: "FX amendments (re-invoiced)" })}</span>
                  <span data-testid="freeze-fx-amend-total" className="text-teal-700 dark:text-teal-300 font-bold">
                    {t("ui.freeze.confirm.fx_amend_total", { n: fxAmend.reinvoiced_count, vat: Number(fxAmend.total_vat_delta).toFixed(2), defaultValue: `${fxAmend.reinvoiced_count} row(s) · Δ VAT ${Number(fxAmend.total_vat_delta).toFixed(2)}` })}
                  </span>
                </div>
                {(fxAmend.amendments || []).map((a) => (
                  <div key={a.txn_id} data-testid={`freeze-fx-amend-${a.txn_id}`} className="px-3 py-1.5 border-b border-teal-50 dark:border-teal-900/50 last:border-0">
                    <div className="flex justify-between">
                      <span className="text-slate-600 dark:text-slate-300">{a.country || "—"} · {a.txn_id.slice(0, 12)}…</span>
                      <span className="text-teal-700 dark:text-teal-300">Δ VAT {Number(a.vat_delta).toFixed(2)}</span>
                    </div>
                    <div className="text-slate-400 truncate">{t("ui.freeze.confirm.fx_amend_ref", { ref: a.evidence_ref || "—", by: a.decided_by ?? "", defaultValue: `ref ${a.evidence_ref || "—"} — ${a.decided_by ?? ""}` })}</div>
                  </div>
                ))}
              </div>
            )}
            {(coverage?.open_gap_count || 0) > 0 && (
              <p className="text-amber-600 dark:text-amber-400" data-testid="freeze-coverage-warning">
                {t("ui.freeze.confirm.coverage_warning", {
                  defaultValue: "You still have unacknowledged coverage gaps. You can freeze anyway, but they will be recorded as open on the filing pack.",
                })}
              </p>
            )}
            {hasNetNegative && (
              <div
                data-testid="net-negative-advisory"
                className="border border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-950/40 rounded-sm px-3 py-2 space-y-2"
              >
                <p className="text-amber-800 dark:text-amber-200">
                  <strong>{t("ui.freeze.confirm.repayment_title", { defaultValue: "Repayment return." })}</strong>{" "}
                  {t("ui.freeze.confirm.repayment_body", {
                    countries: negativeCountries.map((c) => c.country).join(", "),
                    defaultValue: `Credit notes exceed sales this period for ${negativeCountries.map((c) => c.country).join(", ")}, so the output VAT is negative. This is legitimate (§17 UStG) — confirm you intend to file a repayment/credit position.`,
                  })}
                </p>
                <label className="flex items-center gap-2 text-amber-900 dark:text-amber-100 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="net-negative-confirm-checkbox"
                    checked={repaymentConfirmed}
                    onChange={(e) => setRepaymentConfirmed(e.target.checked)}
                  />
                  {t("ui.freeze.confirm.repayment_confirm", { defaultValue: "I confirm this is intended to be a repayment return." })}
                </label>
              </div>
            )}
            {hasRefundChannelMismatch && (
              <div
                data-testid="refund-channel-mismatch-advisory"
                className="border border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-950/40 rounded-sm px-3 py-2 space-y-2"
              >
                <p className="text-amber-800 dark:text-amber-200">
                  <strong>{t("ui.freeze.confirm.mismatch_title", { defaultValue: "Refund channel mismatch." })}</strong>{" "}
                  {t("ui.freeze.confirm.mismatch_body", {
                    count: refundChannelMismatchCount,
                    defaultValue: `${refundChannelMismatchCount} refund(s) inherited the channel of the supply they reverse, but the routing rules would place their own attributes on a different return. The inherited channel is used for filing — but the original supply may carry an out-of-date routing. Review each in Step 4 and confirm before filing.`,
                  })}
                </p>
                <label className="flex items-center gap-2 text-amber-900 dark:text-amber-100 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="refund-channel-mismatch-confirm-checkbox"
                    checked={mismatchAcknowledged}
                    onChange={(e) => setMismatchAcknowledged(e.target.checked)}
                  />
                  {t("ui.freeze.confirm.mismatch_confirm", {
                    defaultValue: "I have reviewed the flagged refund(s) and confirm the inherited channel is correct.",
                  })}
                </label>
              </div>
            )}
            {freezeError && (
              <p className="text-red-600 dark:text-red-400" data-testid="freeze-confirm-error">{String(freezeError)}</p>
            )}
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="freeze-confirm-cancel">{t("ui.freeze.confirm.cancel", { defaultValue: "Cancel" })}</AlertDialogCancel>
        <AlertDialogAction
          data-testid="freeze-confirm-submit"
          onClick={(e) => { e.preventDefault(); doFreeze(); }}
          disabled={freezing || (hasNetNegative && !repaymentConfirmed) || (hasRefundChannelMismatch && !mismatchAcknowledged)}
        >
          {freezing ? t("ui.freeze.confirm.freezing", { defaultValue: "Freezing…" }) : t("ui.freeze.confirm.submit", { defaultValue: "Freeze & lock" })}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
  );
};

export default FreezeConfirmDialog;
